import React, {useState} from 'react';
import {ButtonGroup, Modal} from '@/components/atoms';
import {useRewards} from '@/hooks/useRewards';
import {
  REVOKE_BUTTON_VISIBLE_STATUS,
  REVOKE_BUTTON_DISABLED_STATUS,
  DUPLICATE_BUTTON_ENABLED_STATUS
} from '@/utils/constants/rewardsConstants';
import {MODAL_TYPE} from '@/utils/constants/globalStyle';
import {SECTION_TITLES} from '../common/ReviewConstants';
const {DUPLICATE_REWARD, DOWNLOAD_REWARD, REVOKE} = SECTION_TITLES.DISPLAY;
/**
 * RewardReview action buttons (Revoke / Duplicate / Download) + revoke modal.
 * Encapsulates all button-related logic so it can be reused across pages.
 */
const ActionButtons = () => {
  const {formData, handleRevoke, handleDuplicate} = useRewards();
  const [isRevokeModalOpen, setIsRevokeModalOpen] = useState(false);

  const normalizedStatus = formData?.status?.toUpperCase?.();

  // Determine if Revoke button should be visible (only for PUBLISHED and ACTIVE)
  const shouldShowRevokeButton =
    normalizedStatus && REVOKE_BUTTON_VISIBLE_STATUS.includes(normalizedStatus);

  // Determine if Revoke button should be disabled (for EXPIRED, DRAFT, REVOKED)
  const isRevokeButtonDisabled =
    normalizedStatus &&
    REVOKE_BUTTON_DISABLED_STATUS.includes(normalizedStatus);

  // Determine if Duplicate button should be enabled (for non-Draft statuses)
  const isDuplicateEnabled =
    normalizedStatus &&
    DUPLICATE_BUTTON_ENABLED_STATUS.includes(normalizedStatus);

  // Handle revoke button click
  const handleRevokeClick = () => {
    setIsRevokeModalOpen(true);
  };

  // Handle revoke modal primary button (Yes)
  const onClickRevokePrimaryButton = async () => {
    await handleRevoke();
    setIsRevokeModalOpen(false);
  };

  // Handle revoke modal secondary button (No)
  const onClickRevokeSecondaryButton = () => {
    setIsRevokeModalOpen(false);
  };

  // Handle revoke modal close
  const handleRevokeModalClose = () => {
    setIsRevokeModalOpen(false);
  };

  // Build button items with conditional Revoke button
  const buttonItems = [];

  // Show Revoke button only for PUBLISHED and ACTIVE statuses (enabled)
  // For EXPIRED, DRAFT, REVOKED: show but disabled (defensive approach)
  if (shouldShowRevokeButton || isRevokeButtonDisabled) {
    buttonItems.push({
      icon: 'delete',
      label: REVOKE,
      onClick: handleRevokeClick,
      disabled: isRevokeButtonDisabled // Disabled for EXPIRED, DRAFT, REVOKED
    });
  }

  buttonItems.push(
    {
      icon: 'copy',
      label: DUPLICATE_REWARD,
      onClick: handleDuplicate,
      disabled: !isDuplicateEnabled
    },
    {
      icon: 'download',
      label: DOWNLOAD_REWARD,
      onClick: () => {},
      disabled: !isDuplicateEnabled
    }
  );

  return (
    <>
      <ButtonGroup
        uiSchema={{
          items: buttonItems
        }}
        isDark
        id='reward-review-action-buttons'
      />

      {isRevokeModalOpen ? (
        <Modal
          type={MODAL_TYPE.FLUID}
          showModal={isRevokeModalOpen}
          onCloseModal={handleRevokeModalClose}
          title='Are you sure you want to revoke this reward?'
          text='Revoke reward is a permanent action and cannot be undone.'
          showPrimaryButton={true}
          primaryButtonText='Yes'
          onClickPrimaryButton={onClickRevokePrimaryButton}
          showSecondaryButton={true}
          secondaryButtonText='No'
          onClickSecondaryButton={onClickRevokeSecondaryButton}
          footerProps={{
            size: 'medium',
            type: 'primary'
          }}
          isDark={true}
        />
      ) : null}
    </>
  );
};

export default ActionButtons;
